<script setup lang="ts">
import { useNuxtApp } from "nuxt/app"
import { computed, ref, watch } from "vue"
import { Doughnut } from "vue-chartjs"
import { ArcElement, Chart as ChartJS, Legend, Title, Tooltip } from "chart.js"
import { storeToRefs } from "pinia"
import { useEpciCommuneStore } from "~/store"

ChartJS.register(ArcElement, Tooltip, Legend, Title)

const { $api, $translate, $getColor } = useNuxtApp()

const epciCommuneStore = useEpciCommuneStore()
const { currentEpciCommune } = storeToRefs(epciCommuneStore)

const communesFilles = computed(() => currentEpciCommune.value?.accessible_communes || [])

function navigateToCommune(commune) {
    epciCommuneStore.setSelectedEpciCommune({
        id: commune.id,
        name: commune.name,
        v: commune.name,
        mission_id: null,
        accessible_communes: [],
    })
}

const stats = ref({})
const loadingStats = ref(false)
const year = ref(null)

const recordsChart = ref({})
const measurementChart = ref({})

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "75%",
    plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
    },
}

function fetchStats() {
    if (!$api)
        return
    loadingStats.value = true

    const epciCommuneId = currentEpciCommune.value?.id
    const params = new URLSearchParams()

    if (epciCommuneId)
        params.append("epci_commune_id", epciCommuneId)
    if (year.value)
        params.append("year", year.value)

    const queryString = params.toString()

    $api.setRequest({
        url: `stats${queryString ? `?${queryString}` : ""}`,
        method: "GET",
    }).then((r) => {
        stats.value = r || {}
        loadingStats.value = false
    }).catch(() => {
        loadingStats.value = false
    })
}

fetchStats()

watch(year, fetchStats)
watch(currentEpciCommune, fetchStats)

watch(stats, (newVal) => {
    if (newVal.records_count) {
        recordsChart.value = {
            labels: Object.keys(newVal.records_count).map(l => $translate(`dossiers.status.${l}`)),
            datasets: [{
                data: Object.values(newVal.records_count),
                backgroundColor: Object.keys(newVal.records_count).map(k => $getColor(`dossiers.status.${k}`)),
                borderWidth: 0,
            }],
        }
    }
    if (newVal.records) {
        measurementChart.value = {
            labels: Object.keys(newVal.records).map(l => $translate(`dossiers.status.${l}`)),
            datasets: [{
                data: Object.values(newVal.records),
                backgroundColor: Object.keys(newVal.records).map(k => $getColor(`dossiers.status.${k}`)),
                borderWidth: 0,
            }],
        }
    }
}, { deep: true })

const years = []
let thisYear = 2022
const currentYear = new Date().getFullYear()
while (thisYear <= currentYear) {
    years.push(thisYear++)
}

const totalRecords = computed(() => {
    if (!stats.value.records_count)
        return 0
    return Object.values(stats.value.records_count).reduce((sum, val) => sum + Number(val), 0)
})

const totalMeasurement = computed(() => {
    if (!stats.value.records)
        return 0
    return Object.values(stats.value.records).reduce((sum, val) => sum + Number(val), 0).toFixed(2)
})

const recordsLegendItems = computed(() => {
    if (!stats.value.records_count)
        return []
    return Object.keys(stats.value.records_count).map(k => ({
        label: $translate(`dossiers.status.${k}`),
        color: $getColor(`dossiers.status.${k}`),
    }))
})

const measurementLegendItems = computed(() => {
    if (!stats.value.records)
        return []
    return Object.keys(stats.value.records).map(k => ({
        label: $translate(`dossiers.status.${k}`),
        color: $getColor(`dossiers.status.${k}`),
    }))
})

const roomsData = computed(() => {
    if (!stats.value.count_by_room)
        return []
    const total = totalRecords.value || 1

    return Object.entries(stats.value.count_by_room)
        .map(([name, count]) => ({
            name,
            count,
            percentage: (Number(count) / total) * 100,
        }))
        .sort((a, b) => b.count - a.count)
})

const currentDate = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
</script>

<template>
    <v-container fluid style="background-color: #F8FAFC; min-height: 100vh;">
        <div class="d-flex flex-column flex-md-row justify-space-between align-md-end mb-6">
            <div>
                <div class="text-h4 font-weight-bold text-primary">
                    Tableau de bord
                </div>
            </div>
            <div class="d-flex align-center mt-4 mt-md-0">
                <div class="text-body-2 text-grey mr-6 d-none d-sm-block">
                    Données actualisées le {{ currentDate }}
                </div>
                <div style="width: 200px">
                    <v-select
                        v-model="year"
                        :items="years"
                        placeholder="Filtrer par année"
                        density="compact"
                        variant="solo-filled"
                        flat
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-calendar"
                    />
                </div>
            </div>
        </div>

        <v-skeleton-loader v-if="loadingStats" height="200" type="card" class="mb-6" />

        <v-card v-else class="pa-6 mb-6" elevation="1" rounded="lg">
            <div class="text-h6 font-weight-bold mb-6">
                Statistiques sur l'année
            </div>
            <v-row>
                <v-col cols="12" md="4">
                    <div class="text-overline text-grey mb-4">
                        NOMBRE DE DOSSIERS
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Archived') }" /> Archivés
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records_count?.Archived || 0 }}
                        </div>
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Transferred') }" /> Versés
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records_count?.Transferred || 0 }}
                        </div>
                    </div>
                    <div class="d-flex justify-space-between pb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Destroyed') }" /> Détruits
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records_count?.Destroyed || 0 }}
                        </div>
                    </div>
                </v-col>

                <v-col cols="12" md="4">
                    <div class="text-overline text-grey mb-4">
                        MÉTRAGE (M)
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Archived') }" /> Archivés
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records?.Archived || 0 }}
                        </div>
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Transferred') }" /> Versés / Sortis
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records?.Released || stats.records?.Transferred || 0 }}
                        </div>
                    </div>
                    <div class="d-flex justify-space-between pb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Destroyed') }" /> Détruits
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records?.Destroyed || 0 }}
                        </div>
                    </div>
                </v-col>

                <v-col cols="12" md="4">
                    <div class="text-overline text-grey mb-4">
                        STOCKAGE PHYSIQUE
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div>Total Salles</div>
                        <div class="font-weight-bold">
                            {{ stats.rooms || 0 }}
                        </div>
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div>Capacité globale</div>
                        <div class="font-weight-bold">
                            {{ stats.capacity || 0 }} m
                        </div>
                    </div>
                    <div class="d-flex justify-space-between pb-2">
                        <div>Remplissage estimé</div>
                        <div class="font-weight-bold" :class="{ 'text-error': (stats.measurement_factor * 100) > 90, 'text-primary': (stats.measurement_factor * 100) <= 90 }">
                            {{ stats.measurement_factor ? Math.round(stats.measurement_factor * 100) : 0 }} %
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-card>

        <v-row>
            <v-col cols="12" :lg="communesFilles.length ? '3' : '4'">
                <v-card class="pa-6 fill-height d-flex flex-column" elevation="1" rounded="lg">
                    <div class="text-h6 font-weight-bold mb-4 text-center">
                        Nombre de dossiers
                    </div>
                    <v-skeleton-loader v-if="loadingStats" height="200" type="image" />
                    <template v-else>
                        <div class="chart-container flex-grow-1 my-4">
                            <Doughnut v-if="recordsChart?.labels?.length > 0" :data="recordsChart" :options="chartOptions" />
                            <div class="chart-center-text">
                                <div class="text-h4 font-weight-bold">
                                    {{ totalRecords }}
                                </div>
                                <div class="text-caption text-grey text-uppercase">
                                    TOTAL
                                </div>
                            </div>
                        </div>
                        <v-row class="mt-4" dense>
                            <v-col v-for="item in recordsLegendItems" :key="item.label" cols="6" class="d-flex align-center text-body-2">
                                <span class="legend-dot" :style="{ backgroundColor: item.color }" /> {{ item.label }}
                            </v-col>
                        </v-row>
                    </template>
                </v-card>
            </v-col>

            <v-col cols="12" :lg="communesFilles.length ? '3' : '4'">
                <v-card class="pa-6 fill-height d-flex flex-column" elevation="1" rounded="lg">
                    <div class="text-h6 font-weight-bold mb-4 text-center">
                        Métrage
                    </div>
                    <v-skeleton-loader v-if="loadingStats" height="200" type="image" />
                    <template v-else>
                        <div class="chart-container flex-grow-1 my-4">
                            <Doughnut v-if="measurementChart?.labels?.length > 0" :data="measurementChart" :options="chartOptions" />
                            <div class="chart-center-text">
                                <div class="text-h4 font-weight-bold">
                                    {{ totalMeasurement }}
                                </div>
                                <div class="text-caption text-grey text-uppercase">
                                    MÈTRES
                                </div>
                            </div>
                        </div>
                        <v-row class="mt-4" dense>
                            <v-col v-for="item in measurementLegendItems" :key="item.label" cols="6" class="d-flex align-center text-body-2">
                                <span class="legend-dot" :style="{ backgroundColor: item.color }" /> {{ item.label }}
                            </v-col>
                        </v-row>
                    </template>
                </v-card>
            </v-col>

            <v-col cols="12" :lg="communesFilles.length ? '3' : '4'">
                <v-card class="pa-6 fill-height" elevation="1" rounded="lg">
                    <div class="text-h6 font-weight-bold mb-8">
                        Dossiers par salle
                    </div>
                    <v-skeleton-loader v-if="loadingStats" height="200" type="paragraph" />
                    <div v-else class="overflow-y-auto" style="max-height: 320px; padding-right: 8px;">
                        <div v-if="roomsData.length === 0" class="text-grey text-center mt-10">
                            Aucune donnée
                        </div>
                        <div v-for="(room, index) in roomsData" :key="index" class="mb-5">
                            <div class="d-flex justify-space-between mb-2">
                                <div class="text-body-2 font-weight-medium text-truncate mr-2">
                                    {{ room.name }}
                                </div>
                                <div class="text-body-2 font-weight-bold">
                                    {{ room.count }}
                                </div>
                            </div>
                            <v-progress-linear
                                color="#091426"
                                height="8"
                                :model-value="room.percentage"
                                rounded
                            />
                        </div>
                    </div>
                </v-card>
            </v-col>

            <v-col v-if="communesFilles.length" cols="12" lg="3">
                <v-card class="pa-6 fill-height" elevation="1" rounded="lg">
                    <div class="text-h6 font-weight-bold mb-4">
                        Communes filles
                    </div>
                    <div class="overflow-y-auto" style="max-height: 320px; padding-right: 8px;">
                        <div
                            v-for="commune in communesFilles"
                            :key="commune.id"
                            class="mb-3"
                        >
                            <a
                                class="text-body-2 font-weight-medium text-primary text-decoration-none commune-link"
                                href="#"
                                @click.prevent="navigateToCommune(commune)"
                            >
                                <v-icon size="small" class="mr-1">
                                    mdi-open-in-new
                                </v-icon>
                                {{ commune.name }}
                            </a>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped>
    .border-b {
    border-bottom: 1px solid #e2e8f0 !important;
}

.chart-container {
    position: relative;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.chart-center-text {
    position: absolute;
    text-align: center;
    pointer-events: none;
}

.legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    display: inline-block;
    margin-right: 8px;
}

.opacity-80 {
    opacity: 0.8;
}

.text-primary {
    color: #091426 !important;
}

.commune-link {
    display: block;
    padding: 6px 8px;
    border-radius: 6px;
    transition: background-color 0.15s;
}
.commune-link:hover {
    background-color: #f1f5f9;
}

/* Personnalisation de la scrollbar pour la liste des salles */
.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 10px;
}
</style>
